/**
 * MedListDialog — "Make med list" from a client record's Medications card
 * (med_list tool, the primary path).
 *
 * Builds the company medication list DETERMINISTICALLY from the ONE
 * ClientRecordSnapshot the record page already holds: the client's ACTIVE
 * structured meds are pulled as rows (one per medicine, the same editor as
 * the tool page), ticked by their When on the record (by their directions
 * only when none is ticked), and printed in the five time-of-day sections,
 * and the allergies line + client name + DOB come straight off
 * the record. No new fetch, and NO model call — the meds are already
 * structured, so a probabilistic tool never touches a real dose (the
 * reliability-safe path). The CM reviews (and can adjust the date) before
 * downloading the branded .docx or saving the list as a draft LINKED to this
 * client; a saved list opens in the Medication List tool to continue.
 *
 * Data flows one way: this reads the record and produces a document. It never
 * writes back to the record.
 */

import { type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog } from '@/components/primitives/Dialog';
import { Button, buttonClassName } from '@/components/primitives/Button';
import { DownloadDocxButton } from '@/components/DownloadDocxButton';
import { DocumentPrintPdfButton } from '@/components/DocumentPrintPdfButton';
import { MedListReview, type MedListLinkedClient } from '@/components/aiTools/MedListReview';
import { ErrorNotice } from '@/components/ErrorNotice';
import { useTenant } from '@/lib/auth';
import { aiToolsApi } from '@/lib/api';
import { aiSaveKeyOf, type KeyedError } from '@/lib/errorCopy';
import { practiceTodayIso } from '@/lib/practiceDate';
import { assembleMedListFromRecord, isMedListDateValid, type MedListDoc } from '@/lib/medList';
import type { ClientRecordSnapshot } from '@/lib/clientRecord';

export interface MedListDialogProps {
  record: ClientRecordSnapshot;
  onClose: () => void;
}

export function MedListDialog({ record, onClose }: MedListDialogProps): ReactNode {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  const today = practiceTodayIso();
  // Assemble once from the snapshot; the CM's inline header edits live in state.
  const [doc, setDoc] = useState<MedListDoc>(() => assembleMedListFromRecord(record, today));
  const [savedId, setSavedId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<KeyedError | null>(null);

  const linkedClient: MedListLinkedClient = {
    clientId: record.clientId,
    displayName: record.vitals.displayName,
    dob: record.vitals.dob,
    recordHref: `/t/${tenantSlug}/clients/${record.clientId}`,
  };

  // Save as a draft linked to THIS client. No invocation: the list came off
  // the record, no model ran.
  // The list as edited and the client are read at the press and travel
  // as the variables.
  const saveMutation = useMutation({
    mutationFn: (v: { document: MedListDoc; clientId: string }) =>
      aiToolsApi.saveMedListDraft(tenantSlug, {
        ai_invocation_id: null,
        client_id: v.clientId,
        source_text: null,
        document: v.document,
      }),
    onSuccess: (res) => {
      setSaveError(null);
      setSavedId(res.id);
      void queryClient.invalidateQueries({ queryKey: ['ai-tools', 'drafts', tenantSlug] });
    },
    onError: (e: unknown) => {
      setSavedId(null);
      setSaveError(aiSaveKeyOf(e));
    },
  });

  const dateValid = isMedListDateValid(doc.date, today);

  const footer = (
    <>
      <Button variant="ghost" size="sm" onClick={onClose}>
        Close
      </Button>
      <Button
        variant="secondary"
        onClick={() => saveMutation.mutate({ document: doc, clientId: record.clientId })}
        disabled={!dateValid || saveMutation.isPending || savedId !== null}
        loading={saveMutation.isPending}
        data-testid="med-list-dialog-save"
      >
        {saveMutation.isPending ? 'Saving…' : savedId !== null ? 'Saved' : 'Save as draft'}
      </Button>
      <DocumentPrintPdfButton
        url={aiToolsApi.medListDownloadUrl(tenantSlug)}
        postBody={doc}
        filename="Medication List.docx"
        testId="med-list-dialog-print-pdf"
      />
      <DownloadDocxButton
        url={aiToolsApi.medListDownloadUrl(tenantSlug)}
        postBody={doc}
        filename="Medication List.docx"
        className={buttonClassName('primary', 'md')}
        testId="med-list-dialog-download"
      >
        Download .docx
      </DownloadDocxButton>
    </>
  );

  return (
    <Dialog title="Make a medication list" size="xl" testId="med-list-dialog" onClose={onClose} footer={footer}>
      <p className="mb-3 text-sm text-ink-soft">
        Check it, then download the Word document or save it as a draft.
      </p>
      <MedListReview
        doc={doc}
        onChange={(next) => {
          setDoc(next);
          setSavedId(null);
        }}
        today={today}
        linkedClient={linkedClient}
        careTeam={{ providers: record.providers, pronouns: record.vitals.pronouns ?? null }}
        emptyLabel="Add active medications to the record to build the list."
      />
      {savedId !== null && (
        <p className="mt-3 text-sm text-sage-700" role="status" data-testid="med-list-dialog-saved">
          Saved to your drafts, linked to this client.{' '}
          <Link
            to={`/t/${tenantSlug}/ai-tools/med-list?draft=${encodeURIComponent(savedId)}`}
            className="font-semibold underline underline-offset-2"
          >
            Open in the Medication List tool
          </Link>
        </p>
      )}
      {saveError !== null && (
        <div className="mt-3">
          <ErrorNotice
            surface="med_list_draft_save"
            code={saveError.key}
            vars={saveError.vars}
            data-testid="med-list-dialog-save-error"
          />
        </div>
      )}
    </Dialog>
  );
}
